import { offset, shift, useFloating, FloatingArrow } from "@floating-ui/react";
import clsx from "clsx";
import { FocusTrap } from "focus-trap-react";
import debounce from "lodash/debounce";
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { CSSTransition } from "react-transition-group";

import ShareButtonsBlueskyPopover from "@ui/components/article/ShareButtons/components/ShareButtonsSharePopover/components/ShareButtonsBlueskyPopover";
import ShareButtonsCopyLink from "@ui/components/article/ShareButtons/components/ShareButtonsSharePopover/components/ShareButtonsCopyLink";
import ShareButtonsFacebookPopover from "@ui/components/article/ShareButtons/components/ShareButtonsSharePopover/components/ShareButtonsFacebookPopover";
import ShareButtonsLinkedinPopover from "@ui/components/article/ShareButtons/components/ShareButtonsSharePopover/components/ShareButtonsLinkedinPopover";
import ShareButtonsThreadsPopover from "@ui/components/article/ShareButtons/components/ShareButtonsSharePopover/components/ShareButtonsThreadsPopover";
import ShareButtonsWhatsappPopover from "@ui/components/article/ShareButtons/components/ShareButtonsSharePopover/components/ShareButtonsWhatsappPopover";
import * as shareButtonsStyles from "@ui/components/article/ShareButtons/ShareButtons.css";
import CloseIcon from "@ui/components/icon/CloseIcon";
import ShareIcon from "@ui/components/icon/ShareIcon";
import { ArticleLayout } from "@ui/components/ledes/ledes.types";
import { BREAKPOINTS } from "@ui/config";
import { useIsSmallerThanBreakpoint } from "@voxmedia/duet-utils/use-is-smaller-than-breakpoint";
import { useScrollLock } from "@voxmedia/duet-utils/use-scroll-lock";

import * as styles from "./ShareButtonsSharePopover.css";

export type PopoverButtons =
  | "copy"
  | "facebook"
  | "threads"
  | "linkedin"
  | "bluesky"
  | "whatsapp"
  | "rss";

interface Props {
  url: string;
  title: string;
  layout: ArticleLayout | "stream" | "video";
  popoverButtons: PopoverButtons[];
  popoverClassname?: string;
}

const ShareButtonsSharePopover: FunctionComponent<Props> = ({
  url,
  title,
  layout,
  popoverButtons,
  popoverClassname,
}) => {
  const [popoverOpen, setPopoverOpen] = useState<boolean>(false);

  const { refs, floatingStyles, update, context } = useFloating({
    middleware: [offset(10), shift()],
  });

  const popoverChange = useCallback(
    (open: boolean) => {
      if (open) {
        update();
      }
      setPopoverOpen(open);
    },
    [update],
  );

  const isMobile = useIsSmallerThanBreakpoint(BREAKPOINTS.tablet);
  const opacityLayerRef = useRef<HTMLDivElement | null>(null);
  const arrowRef = useRef(null);

  // Close the popover when clicking outside
  useEffect(() => {
    function close(e: Event) {
      if (
        !refs.floating.current?.contains(e.target as Node) &&
        !(refs.reference.current as HTMLElement).contains(e.target as Node)
      ) {
        popoverChange(false);
      }
    }

    // Close the popover when scrolling significantly
    let lastScrollY = window.scrollY;
    const handleScroll = debounce(() => {
      const scrolled = Math.abs(window.scrollY - lastScrollY);
      if (scrolled > 50) {
        close(new Event("scroll"));
      }
      lastScrollY = window.scrollY;
    }, 100);

    if (popoverOpen) {
      document.addEventListener("click", close);
      document.addEventListener("scroll", handleScroll);
    }

    return () => {
      document.removeEventListener("click", close);
      document.removeEventListener("scroll", handleScroll);
    };
  }, [refs.floating, refs.reference, popoverChange, popoverOpen]);

  // Lock scroll on mobile when popover is open
  useScrollLock(popoverOpen && isMobile);

  /**
   * Renders the share buttons based on the popoverButtons configuration
   */
  const renderShareButtons = () => {
    return popoverButtons.map((buttonType) => {
      switch (buttonType) {
        case "copy":
          return <ShareButtonsCopyLink key={buttonType} url={url} />;
        case "facebook":
          return (
            <ShareButtonsFacebookPopover
              key={buttonType}
              url={url}
              onPopoverClose={() => popoverChange(false)}
            />
          );
        case "threads":
          return (
            <ShareButtonsThreadsPopover
              key={buttonType}
              url={url}
              title={title}
              onPopoverClose={() => popoverChange(false)}
            />
          );
        case "linkedin":
          return (
            <ShareButtonsLinkedinPopover
              key={buttonType}
              url={url}
              onPopoverClose={() => popoverChange(false)}
            />
          );
        case "bluesky":
          return (
            <ShareButtonsBlueskyPopover
              key={buttonType}
              url={url}
              title={title}
              onPopoverClose={() => popoverChange(false)}
            />
          );
        case "whatsapp":
          return (
            <ShareButtonsWhatsappPopover
              key={buttonType}
              url={url}
              title={title}
              onPopoverClose={() => popoverChange(false)}
            />
          );
        case "rss":
          // Note: RSS button would need to be implemented as a popover component
          // For now, we'll skip it since it wasn't in the original popover
          return null;
        default:
          return null;
      }
    });
  };

  return (
    <>
      <button
        onClick={() => popoverChange(!popoverOpen)}
        ref={refs.setReference}
        aria-expanded={popoverOpen}
        aria-haspopup="true"
        aria-label="Share article"
        className={shareButtonsStyles.button[layout]}
      >
        <ShareIcon className={shareButtonsStyles.icon[layout]} />
      </button>

      {/* Mobile opacity layer */}
      <CSSTransition
        in={popoverOpen && isMobile}
        timeout={200}
        nodeRef={opacityLayerRef}
        classNames={{
          enter: shareButtonsStyles.opacityLayerEnter,
          enterActive: shareButtonsStyles.opacityLayerEnterActive,
          exit: shareButtonsStyles.opacityLayerExit,
          exitActive: shareButtonsStyles.opacityLayerExitActive,
        }}
        unmountOnExit
      >
        <div
          ref={opacityLayerRef}
          className={shareButtonsStyles.opacityLayer}
        />
      </CSSTransition>

      {/* Share popover */}
      <CSSTransition
        in={popoverOpen}
        timeout={200}
        nodeRef={refs.floating}
        classNames={{
          enter: shareButtonsStyles.sharePopoverEnter,
          enterActive: shareButtonsStyles.sharePopoverEnterActive,
          exit: shareButtonsStyles.sharePopoverExit,
          exitActive: shareButtonsStyles.sharePopoverExitActive,
        }}
        unmountOnExit
      >
        <FocusTrap
          active={popoverOpen}
          focusTrapOptions={{
            fallbackFocus: `#share-popover-${Date.now()}`,
            escapeDeactivates: true,
            clickOutsideDeactivates: false,
            allowOutsideClick: true,
          }}
        >
          <div
            id={`share-popover-${Date.now()}`}
            style={{
              ...(isMobile ? {} : floatingStyles),
            }}
            ref={refs.setFloating}
            className={clsx(shareButtonsStyles.sharePopover, popoverClassname)}
            role="dialog"
            aria-modal="true"
            aria-label="Share options"
          >
            <FloatingArrow
              ref={arrowRef}
              context={context}
              className={shareButtonsStyles.sharePopoverArrow}
            />
            <div className={shareButtonsStyles.sharePopoverContent}>
              <div className={shareButtonsStyles.shareHeadingContainer}>
                <div className={shareButtonsStyles.shareHeading}>Share on:</div>
                <button onClick={() => popoverChange(false)}>
                  <CloseIcon
                    className={shareButtonsStyles.dismissIcon}
                    width={16}
                    height={16}
                  />
                </button>
              </div>
              <div className={styles.shareOptions}>{renderShareButtons()}</div>
            </div>
          </div>
        </FocusTrap>
      </CSSTransition>
    </>
  );
};

export default ShareButtonsSharePopover;
